import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CalendarClock, Check } from 'lucide-react';

interface ReagendarVisitaPopoverProps {
    /** Título da visita (nome do estabelecimento/lead). */
    titulo: string;
    /** Data de referência (dia atualmente exibido), YYYY-MM-DD. */
    dataAtual: string;
    /** Retângulo do botão âncora (getBoundingClientRect) para posicionar o popover. */
    anchor: DOMRect;
    salvando: boolean;
    onConfirm: (novaData: string) => void;
    onClose: () => void;
}

const isoDate = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const addDays = (n: number) => { const d = new Date(); d.setDate(d.getDate() + n); return isoDate(d); };
const fmtCurto = (iso: string) => new Date(iso + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });

const POPOVER_W = 256;

/** Popover leve (dropdown sobreposto) para reagendar uma única visita direto no calendário. */
const ReagendarVisitaPopover: React.FC<ReagendarVisitaPopoverProps> = ({ titulo, dataAtual, anchor, salvando, onConfirm, onClose }) => {
    const hoje = isoDate(new Date());
    const [data, setData] = useState(addDays(1));
    const ref = useRef<HTMLDivElement>(null);
    const [pos, setPos] = useState<{ top: number; left: number }>({ top: anchor.bottom + 6, left: anchor.right - POPOVER_W });

    // Reposiciona dentro da viewport (acima da âncora se faltar espaço embaixo)
    useLayoutEffect(() => {
        const h = ref.current?.offsetHeight || 320;
        const margin = 8;
        let top = anchor.bottom + 6;
        if (top + h > window.innerHeight - margin) top = Math.max(margin, anchor.top - h - 6);
        let left = anchor.right - POPOVER_W;
        if (left < margin) left = margin;
        if (left + POPOVER_W > window.innerWidth - margin) left = window.innerWidth - POPOVER_W - margin;
        setPos({ top, left });
    }, [anchor]);

    // Fecha com Esc
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    const atalhos: Array<{ label: string; value: string }> = [
        { label: 'Amanhã', value: addDays(1) },
        { label: 'Depois de amanhã', value: addDays(2) },
        { label: 'Próxima semana', value: addDays(7) },
    ];

    return createPortal(
        <div className="fixed inset-0 z-[2100]" onClick={onClose}>
            <div
                ref={ref}
                style={{ top: pos.top, left: pos.left, width: POPOVER_W }}
                className="fixed bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700 p-3.5 animate-fade-in"
                onClick={e => e.stopPropagation()}
            >
                <div className="flex items-center gap-2 mb-2.5">
                    <div className="p-1.5 rounded-lg bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 shrink-0">
                        <CalendarClock size={15} />
                    </div>
                    <div className="min-w-0">
                        <p className="text-xs font-bold text-gray-900 dark:text-white leading-tight">Reagendar visita</p>
                        <p className="text-[10px] text-gray-400 truncate">{titulo}</p>
                    </div>
                </div>

                <div className="grid grid-cols-3 gap-1.5 mb-2.5">
                    {atalhos.map(a => (
                        <button
                            key={a.label}
                            onClick={() => setData(a.value)}
                            className={`px-1.5 py-1.5 rounded-lg text-[10px] font-semibold border transition-all leading-tight ${data === a.value
                                ? 'bg-primary/10 border-primary/40 text-primary'
                                : 'bg-gray-50 dark:bg-gray-700/40 border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-gray-300'}`}
                        >
                            {a.label}
                        </button>
                    ))}
                </div>

                <input
                    type="date"
                    value={data}
                    min={hoje}
                    onChange={e => setData(e.target.value)}
                    className="w-full px-2.5 py-2 text-sm rounded-xl bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary/40 mb-2.5"
                />

                <button
                    onClick={() => data && onConfirm(data)}
                    disabled={!data || salvando}
                    className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-bold rounded-xl bg-primary text-white hover:opacity-90 transition-opacity disabled:opacity-40"
                >
                    <Check size={14} />
                    {salvando ? 'Movendo...' : `Mover p/ ${fmtCurto(data)}`}
                </button>
                <p className="text-[10px] text-gray-400 text-center mt-1.5">de {fmtCurto(dataAtual)} · fica no histórico do lead</p>
            </div>
        </div>,
        document.body
    );
};

export default ReagendarVisitaPopover;
